import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DayOfWeek } from '@vendasta/meetings';
import { SubscriptionList } from '@vendasta/rx-utils';
import moment from 'moment-timezone';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { DailyAvailability } from '../../interface';
import { defaultTimeRange } from '../../utils';
import { ScheduleSettingsViewService } from '../schedule-settings-view.service';
import { ScheduleSettingsService, indexToDayOfWeekMapping } from '../schedule-settings.service';

@Component({
  selector: 'meeting-scheduler-settings-availability-card',
  templateUrl: './availability-card.component.html',
  styleUrls: ['./availability-card.component.scss'],
  standalone: false,
})
export class AvailabilityCardComponent implements OnInit, OnDestroy {
  @Input() isHostConfigured: boolean;
  @Output() availabilitySaved: EventEmitter<void> = new EventEmitter<void>();

  loadingAvailabilityForm = true;
  availabilityForm: UntypedFormGroup;

  timeOptions: Date[] = [];

  readonly indexToDayOfWeekMapping = indexToDayOfWeekMapping;
  timezone: string;
  savingAvailabilityForm = false;

  private subscriptions = SubscriptionList.new();

  get formData(): UntypedFormArray {
    return <UntypedFormArray>this.availabilityForm.get('daysOfWeek');
  }

  constructor(
    private readonly alertService: SnackbarService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly scheduledMeetingsService: ScheduleSettingsService,
    private readonly scheduleSettingsViewService: ScheduleSettingsViewService,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
  ) {}

  ngOnInit(): void {
    this.timezone = moment.tz.guess();
    this.timeOptions = this.scheduledMeetingsService.timeOptions;
    this.setupAvailabilityForm();
  }

  completeSetup(): void {
    if (this.availabilityForm.invalid) {
      this.alertService.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_TIME_REQUIRED_ERROR');
      return;
    }
    const selectedDaysOfWeek = this.getSelectedDaysFromForm();
    const start = new Date(this.availabilityForm.value.selectedStartTime);
    const end = new Date(this.availabilityForm.value.selectedEndTime);

    const availability: DailyAvailability = { days: new Map() };
    selectedDaysOfWeek.forEach((day) => {
      availability.days.set(day, [{ start, end }]);
    });
    this.savingAvailabilityForm = true;
    const setAvailabiltySubscription = this.scheduledMeetingsService
      .setAvailability(availability, this.timezone)
      .subscribe({
        next: () => {
          this.scheduleSettingsViewService.resetStateStorage();
          if (!selectedDaysOfWeek || selectedDaysOfWeek.length === 0) {
            this.alertService.openSuccessSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.BLANK_AVAILABILITY_SAVED');
          } else {
            this.alertService.openSuccessSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_SAVED');
          }
          this.savingAvailabilityForm = false;
          this.meetingSchedulerStoreService.markHostAsConfigured();
          this.availabilitySaved.emit();
        },
        error: () => {
          this.alertService.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_SAVED_ERROR');
        },
      });

    this.subscriptions.addSub(setAvailabiltySubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  private getSelectedDaysFromForm(): DayOfWeek[] {
    return this.availabilityForm.value.daysOfWeek
      .map((v, i) => (v ? indexToDayOfWeekMapping[i].id : null))
      .filter((v) => v !== null);
  }

  private async setupAvailabilityForm(): Promise<void> {
    this.loadingAvailabilityForm = true;
    const availability = await this.scheduledMeetingsService.loadInitialAvailability();
    const firstDay = availability.days.values().next().value;
    const start = this.scheduledMeetingsService.getEquivalentTimeOption(
      (firstDay && firstDay[0]?.start) || defaultTimeRange().start,
    );
    const end = this.scheduledMeetingsService.getEquivalentTimeOption(
      (firstDay && firstDay[0]?.end) || defaultTimeRange().end,
    );
    this.availabilityForm = this.formBuilder.group({
      selectedStartTime: this.formBuilder.control(start, [Validators.required]),
      selectedEndTime: this.formBuilder.control(end, [Validators.required]),
      daysOfWeek: this.formBuilder.array([
        new UntypedFormControl(availability.days.get(DayOfWeek.MONDAY)),
        new UntypedFormControl(availability.days.get(DayOfWeek.TUESDAY)),
        new UntypedFormControl(availability.days.get(DayOfWeek.WEDNESDAY)),
        new UntypedFormControl(availability.days.get(DayOfWeek.THURSDAY)),
        new UntypedFormControl(availability.days.get(DayOfWeek.FRIDAY)),
        new UntypedFormControl(availability.days.get(DayOfWeek.SATURDAY)),
        new UntypedFormControl(availability.days.get(DayOfWeek.SUNDAY)),
      ]),
    });
    this.loadingAvailabilityForm = false;
  }

  onTimezoneChanged(timezone: string): void {
    this.timezone = timezone;
  }
}
